/**
 * LeaseAccessPage — Batch #14.11 (Anti Check Point)
 *
 * Page intermédiaire affichée AVANT la page de signature réelle.
 * Quand Check Point ou un autre bot d'analyse de sécurité "pre-fetch" le lien
 * dans le courriel, il atterrit ici. Cette page :
 *  - Ne consomme RIEN du token
 *  - Affiche un message rassurant à l'humain
 *  - Demande UN clic explicite pour passer à /sign/{token}
 *
 * Les bots ne simulent pas le clic → ne consomment pas le token.
 * L'humain voit la page, clique, et accède normalement à la signature.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, Loader2, FileSignature, AlertCircle, Lock, ExternalLink } from "lucide-react";
import { API } from "@/api";


export default function LeaseAccessPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Endpoint LÉGER qui retourne juste un aperçu (signer name + property) — pas le PDF
        const r = await axios.get(`${API}/public/lease-signature/${token}/preview`, { timeout: 20000 });
        if (mounted) setInfo(r.data);
      } catch (e) {
        if (mounted) {
          if (e.response?.status === 404 || e.response?.status === 410) {
            setError(e.response?.data?.detail || "Lien invalide ou expiré.");
          } else {
            setError("Impossible de vérifier le lien. Réessayez ou contactez votre cabinet comptable.");
          }
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const handleAccess = async () => {
    setConfirming(true);
    try {
      // Endpoint POST qui marque le signer comme "human_confirmed" (audit anti-bot)
      // En cas d'échec, on continue quand même vers /sign/ — le signer humain ne doit pas être bloqué
      await axios.post(`${API}/public/lease-signature/${token}/confirm-access`, {}, { timeout: 10000 });
    } catch {
      // Silent — pas bloquant
    }
    navigate(`/sign/${token}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4 p-6">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
        <div className="text-sm text-slate-600">Vérification du lien sécurisé...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white border border-rose-200 rounded-2xl p-8 max-w-md text-center" data-testid="lease-access-error">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Lien indisponible</h1>
          <p className="text-sm text-slate-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
            data-testid="lease-access-error-retry"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-lg w-full overflow-hidden" data-testid="lease-access-card">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white p-6">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              <Shield className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Accès sécurisé à votre bail</h1>
              <p className="text-sm opacity-90">DG Comptabilité — Signature électronique</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3 text-sm text-slate-700">
            <Lock className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p>Bonjour <strong>{info?.signer_name || "—"}</strong>,</p>
              <p className="mt-1 text-slate-600">
                Vous êtes invité(e) à signer électroniquement votre bail TAL pour
                <strong className="text-slate-800"> {info?.property_address || "votre logement"}</strong>
                {info?.unit_label ? <> · Apt <strong>{info.unit_label}</strong></> : null}.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
            <strong>Pourquoi cette étape ?</strong> Pour des raisons de sécurité (protection
            contre les analyses automatiques d'antivirus), nous vous demandons de confirmer
            votre identité humaine en cliquant sur le bouton ci-dessous.
          </div>

          <button
            onClick={handleAccess}
            disabled={confirming}
            className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-sm shadow-md inline-flex items-center justify-center gap-2 transition"
            data-testid="lease-access-continue-btn"
          >
            {confirming ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileSignature className="w-5 h-5" />}
            {confirming ? "Accès en cours..." : "Accéder à mon bail à signer"}
            <ExternalLink className="w-4 h-4 opacity-70" />
          </button>

          <p className="text-[10px] text-slate-400 text-center">
            Lien sécurisé · Expire le {info?.expires_at ? new Date(info.expires_at).toLocaleDateString("fr-CA") : "—"}
            <br />
            En cas de problème : <a href="mailto:DGFiscalFlow@dgcomptabilite.ca" className="underline">DGFiscalFlow@dgcomptabilite.ca</a>
          </p>
        </div>
      </div>
    </div>
  );
}
